import { Alert, Line, Heading, Select, Option, Tooltip } from '@mm/components';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import PageModel from '@mm/state/model/PageModel';
import pluralize from 'pluralize';
import modalActions from '@mm/state/modules/modalsState/actions';
import { CampaignTypeWithMappedPurposeAndDisplayStatus } from '@mm/state/selectors/campaignSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  combinedFeatureFlagsSelector,
  companyFeatureFlagsSelector,
} from '@mm/state/selectors/featureFlagsSelectors';
import { hasSMSNumberSelector } from '@mm/state/selectors/twillio';
import {
  getTwilioAvailable,
  selectActiveChatbot,
} from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { first } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import CheckBox from '../../../../../../common/components/Checkbox';
import { TooltipGuard } from '../../../../../../modules/permissions/RequiresPermissions';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { AudienceSelector } from '../../../components/AudienceSelector/AudienceSelector';
import { blasterCardTypes } from '../../../components/BlastTypeSelector/blasterCardTypes';
import { BlastTypeSelector } from '../../../components/BlastTypeSelector/BlastTypeSelector';
import CalendarBlock from '../../../components/CalendarBlock/CalendarBlock';
import { filterCampaignWidgets } from '../../../components/helpers';
import {
  CampaignErrors,
  OTN_PURPOSE_ID,
  SUBSCRIPTION_MESSAGING_PURPOSE,
} from '../ChatBlasterDetailsPanel';
import './ChatBlasterSteps.pcss';
import { PreviewChannelOption } from '../ChatBlasterPreviewByChannels/ChatBlasterPreviewByChannels';
import { hasConnectedRealFacebookPageSelector } from '@mm/state/selectors/userSelector';

interface Props {
  errors: CampaignErrors;
  version?: string;
  purposeId?: number;
  campaign: CampaignTypeWithMappedPurposeAndDisplayStatus;
  dialogue: PageModel;
  targetAudienceSize: number;
  renderSendButton?(classnames: string): void;
  onPlatformsChange(v: PlatformType[]): void;
  onPurposeChange(purposeId: number, tagName?: string): void;
  onTagNameChange(tagName?: string): void;
  onAudienceSelect(audienceId?: number | null): void;
  onSelectedDateChange(time: moment.Moment): void;
  onRecipientTimeZoneChange(shouldUse: boolean): void;
  onSendImmediatelyChange(sendImmediately: boolean): void;
  setActivePreview: (channel: PreviewChannelOption) => void;
}

const getPlatforms = (platformMap: {
  facebook: boolean;
  sms: boolean;
}): PlatformType[] => {
  const platforms: PlatformType[] = [];

  if (platformMap.facebook) {
    platforms.push('facebook');
  }

  if (platformMap.sms) {
    platforms.push('sms');
  }

  return platforms;
};
const getSelectedDate = (
  campaign: CampaignTypeWithMappedPurposeAndDisplayStatus,
) => {
  const { schedulersAttributes } = campaign;

  const { scheduledAt } = first(schedulersAttributes) || {};

  if (scheduledAt) {
    return moment(scheduledAt);
  }

  return moment().add(1, 'day').hours(9).startOf('hour');
};

const noop = () => {};

export const ChatBlasterSteps = (props: Props) => {
  const {
    errors,
    version,
    campaign,
    purposeId,
    dialogue,
    renderSendButton,
    targetAudienceSize,
    onPlatformsChange = noop,
    onPurposeChange = noop,
    onTagNameChange = noop,
    onAudienceSelect = noop,
    onSelectedDateChange = noop,
    onRecipientTimeZoneChange = noop,
    onSendImmediatelyChange = noop,
    setActivePreview,
  } = props;

  const isUserPageConnected = useSelector(hasConnectedRealFacebookPageSelector);

  const chatbot = useSelector(selectActiveChatbot);
  const dispatch: Dispatch<StoreState> = useDispatch();
  const twilioFeatFlag = useSelector(companyFeatureFlagsSelector).twilio;
  const twilioAvailableFlag = useSelector(getTwilioAvailable);
  const smsSupported = twilioFeatFlag || twilioAvailableFlag;
  const hasSMSNumber = useSelector(hasSMSNumberSelector);

  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  const router = useRouter<{ dialogueId: string }>();
  const dialogueId = Number(dialogue?.id || router.match.params.dialogueId);
  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(dialogueId, state.widget.entities),
  );

  const isSmsDisabled = useSelector(
    combinedFeatureFlagsSelector,
  ).twillio_campaigns_disabled;

  useEffect(() => {
    if (campaign.platforms) {
      setActivePreview(campaign.platforms[0] as PreviewChannelOption);
    }
  }, [campaign.platforms]);

  const onSMSToggle = (platforms: PlatformType[]) => {
    const enable = platforms.includes('sms');

    const onCloseHandler = (ok: boolean) => {
      if (ok && chatbot) {
        const path = BotBuilderRoutes.settingsSms.getLink({
          chatbotId: chatbot.id,
        });
        router.history.push(path);
      }
    };

    if (!hasSMSNumber && enable) {
      dispatch(
        modalActions.openGeneralConfirmModal({
          headerText: 'Activate SMS',
          messageText: 'You need to enable SMS to send Chat Blast through SMS.',
          okButtonText: 'Activate',
          onCloseHandler,
        }),
      );
    } else {
      onPlatformsChange(platforms);
    }
  };

  const platforms = {
    facebook: !!campaign.platforms?.includes('facebook'),
    sms: !!campaign.platforms?.includes('sms'),
  };

  const isCanceled = campaign.status === 'canceled';
  const isReadOnly = campaign.active || isCanceled;

  const selectedDate = getSelectedDate(campaign);
  const sendImmediately = campaign.scheduleType === 'none';
  const isSelectedDateValid =
    sendImmediately || moment().isBefore(selectedDate);
  const isApproved =
    purposeId !== SUBSCRIPTION_MESSAGING_PURPOSE
      ? true
      : activeFacebookPage &&
        activeFacebookPage.subscriptionMessagingStatus === 'approved';

  const notificationPurposes = useSelector(
    (state: StoreState) => state.campaignPurposes.purposes,
  );
  const isOtn = purposeId === OTN_PURPOSE_ID;

  const options: (Option & { id: PlatformType })[] = [
    {
      id: 'facebook',
      label: (
        <TooltipGuard
          position="top"
          classNameContent="chat-blaster-steps__guard"
        >
          {() => <>Facebook Messenger</>}
        </TooltipGuard>
      ),
      disabled: !isUserPageConnected,
      toString: () => 'Facebook Messenger',
    },
  ];

  const selected = options.filter(v => campaign.platforms?.includes(v.id));

  const onPlatformChange = opts => {
    const platforms = opts.map(opt => opt.id);

    if (platforms && platforms.includes('sms')) {
      return onSMSToggle(platforms);
    }

    onPlatformsChange(platforms);
  };

  const specifyPlatform = {
    id: 'platform',
    header: 'Channels',
    children: (
      <div className="chat-blaster-steps__check-area">
        {errors.platforms && (
          <Alert
            className="chat-blaster-steps__field-error"
            type="error"
            showCloseIcon={false}
          >
            {errors.platforms}
          </Alert>
        )}

        <Select
          options={options}
          selected={selected}
          onChange={onPlatformChange}
        />
      </div>
    ),
  };

  const { smsOnly } = filterCampaignWidgets(campaign, widgets);

  const defaultConfigs = [
    {
      id: 'schedule',
      header: 'Send this message now or later',
      children: (
        <>
          <CalendarBlock
            allowScheduleTypeChange={!isReadOnly}
            isSelectedDateValid={isSelectedDateValid}
            selectedDate={selectedDate}
            setSelectedDate={onSelectedDateChange}
            useRecipientTimeZone={campaign.scheduleType === 'timezone'}
            setUseRecipientTimeZone={onRecipientTimeZoneChange}
            sendImmediately={sendImmediately}
            setSendImmediately={onSendImmediatelyChange}
          />
          {renderSendButton?.('chat-blaster-steps__send-button')}
        </>
      ),
    },
  ];

  const configs = [
    ...(smsSupported ? [specifyPlatform] : []),
    {
      id: 'audience',
      header: 'Audience',
      children: (
        <>
          {errors.audience && (
            <Alert
              className="chat-blaster-steps__field-error"
              type="error"
              showCloseIcon={false}
            >
              {errors.audience}
            </Alert>
          )}

          <div className="chat-blaster-details__audience-fields">
            <div className="chat-blaster-details__audience-selector">
              <AudienceSelector
                autoselect
                selectedAudienceId={campaign.audienceId}
                onAudienceSelected={onAudienceSelect}
                disabled={isReadOnly}
                onCancel={onAudienceSelect}
              />
            </div>
          </div>
          <div className="chat-blaster-details__audience-contacts-counter">
            <div className="chat-blaster-details__audience-contacts-counter--text">
              {purposeId === blasterCardTypes.promoMessage.purposeId || isOtn
                ? 'Reachable Contacts:'
                : 'Contacts:'}
            </div>
            <div className="chat-blaster-details__audience-contacts-counter--number">
              {campaign.audienceId ? targetAudienceSize : 'N/A'}
            </div>
          </div>
          {platforms.sms && campaign.audienceId && targetAudienceSize > 1000 && (
            <Alert
              type="warning"
              className="chat-blaster-details__audience-contacts-alert"
              showCloseIcon={false}
            >
              Sending a blast to {targetAudienceSize} contacts could take over{' '}
              {Math.ceil(targetAudienceSize / 1000)}{' '}
              {pluralize('hour', Math.ceil(targetAudienceSize / 1000))}
            </Alert>
          )}
        </>
      ),
    },
    ...(smsOnly
      ? []
      : [
          {
            id: 'type',
            header: 'Type of content',
            children: (
              <BlastTypeSelector
                purpose={purposeId}
                purposeName={campaign.purposeName}
                onPurposeChange={onPurposeChange}
                notificationPurposes={notificationPurposes}
                selectedTagName={campaign.tags?.[0]?.name || ''}
                setSelectedTagName={onTagNameChange}
                facebookPageId={activeFacebookPage?.id}
                widgets={widgets}
              />
            ),
          },
        ]),
    ...defaultConfigs,
  ];

  return (
    <div className="chat-blaster-steps">
      {configs.map(config => {
        return (
          <div key={config.id}>
            <Heading size={5} className="chat-blaster-steps__header">
              <div>{config.header}</div>
            </Heading>
            <div>{config.children}</div>
            {config.id !== 'schedule' && (
              <Line className="chat-blaster-steps-line" />
            )}
          </div>
        );
      })}
    </div>
  );
};
